import { useEffect, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { $accountCardListStore, $currenciesStore, $messageCentreStore } from '@lib/store';
import { BaseInput, Button, CustomFile, FileUpload, Modal, Select, Textarea } from '@shared/widgets';
import { AccountType } from '@shared/api';

interface IForm {
  from: string;
  to: string;
  bicSwiftCode: string;
  name: string;
  currency: string;
  amount: string;
  reference: string;
  otherDetails: string;
  attachment: Blob | null;
}
const DEFAULT_VALUES: IForm = {
  from: '',
  to: '',
  bicSwiftCode: '',
  name: '',
  currency: '',
  amount: '',
  reference: '',
  otherDetails: '',
  attachment: null,
};

interface ManualPaymentRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ManualPaymentRequestModal = (props: ManualPaymentRequestModalProps) => {
  const accountStore = useUnit($accountCardListStore.store);
  const currencyStore = useUnit($currenciesStore.store);

  const { control, formState, handleSubmit, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });

  const { t } = useTranslation();

  useEffect(() => {
    Promise.allSettled([$accountCardListStore.events.getAccounts({ AccountType: AccountType.Payment }), $currenciesStore.events.getCurrencies()]);
    return () => {
      $accountCardListStore.events.clear();
    };
  }, []);

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: IForm) => {
    const formData = new FormData();
    data.attachment && formData.append('file', data.attachment);
    const body = `
			${data.from ? `From: ${data.from} \n` : ''}
			${data.to ? `To: ${data.to} \n` : ''}
			${data.bicSwiftCode ? `BIC / SWIFT Code: ${data.bicSwiftCode} \n` : ''} 
			${data.name ? `Beneficiary Name / Account Name: ${data.name} \n` : ''} 
			${data.currency ? `Currency: ${data.currency} \n` : ''} 
			${data.amount ? `Amount: ${data.amount} \n` : ''} 
			${data.reference ? `Reference: ${data.reference} \n` : ''} 
			${data.otherDetails ? `Other details: ${data.otherDetails} \n` : ''} 
			`.trim();

    $messageCentreStore.events.createNewThreadWithMessage({ subject: 'Manual Payment Request', body, file: data.attachment ? formData : undefined });
    closeHandler();
  };

  const accountOptions = useMemo(
    () => accountStore.data.map((account) => ({ label: account.accountNumber, value: account.accountNumber })),
    [accountStore.data],
  );
  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title='Manual Payment Request' width='380px'>
      <form className='flex flex-col gap-2' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='from'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <Select
              label='From'
              items={accountOptions}
              nullValue={{ isEnable: false, text: 'From' }}
              required
              error={error?.message}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name='to'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput {...field} label='To' placeholder='IBAN/Account' required error={error?.message} />
          )}
        />
        <Controller
          name='bicSwiftCode'
          control={control}
          render={({ field }) => <BaseInput {...field} label='BIC / SWIFT Code' placeholder='BIC / SWIFT Code' />}
        />
        <Controller
          name='name'
          control={control}
          render={({ field }) => <BaseInput {...field} label='Beneficiary Name / Account Name' placeholder='Beneficiary Name / Account Name' />}
        />
        <Controller
          name='currency'
          control={control}
          render={({ field }) => (
            <Select
              label='Currency'
              items={currencyStore.data.map((currency) => ({ label: currency, value: currency }))}
              nullValue={{ isEnable: false, text: 'Currency' }}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller name='amount' control={control} render={({ field }) => <BaseInput {...field} label='Amount' placeholder='Amount' />} />
        <Controller
          name='reference'
          control={control}
          render={({ field }) => <BaseInput {...field} label='Reference number' placeholder='Reference number' />}
        />
        <Controller
          name='otherDetails'
          control={control}
          render={({ field }) => (
            <Textarea
              textareaClassName='mb-0'
              fitContent
              label='Other details'
              placeholder='Other details'
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name='attachment'
          control={control}
          render={({ field: { value, onChange } }) => (
            <FileUpload label='Attachment' value={value as unknown as CustomFile} onChange={onChange} onDelete={() => onChange(null)} />
          )}
        />
        <Button className='mt-6' color='primary' type='submit' disabled={formState.isSubmitting}>
          Submit
        </Button>
      </form>
    </Modal>
  );
};
